import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  User, Building2, Shield, Lock, Bell, Palette, Link2, ScrollText, Award,
  UserCog, Save, Loader2, ChevronLeft, HelpCircle, Eye, EyeOff,
  Globe, Clock, Mail, Phone, MapPin, FileText, AlertTriangle, CheckCircle2,
  Download, Trash2, Search, Calendar, Activity, Users, Settings, Filter, X,
  Plus, Pencil, ChevronRight, Key, Monitor, Smartphone, RefreshCw, RefreshCcw,
  Webhook, Database, Upload, Image, Info, Layers, BarChart3, Check
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Progress } from "../../components/ui/progress";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import api from "../../lib/api";
import { cn } from "../../lib/utils";
import { useAuth } from "../../context/AuthContext";
import { useBranding } from "../../context/BrandingContext";

const HelpTip = ({ title, children }) => (
  <div className="p-3 rounded-lg bg-blue-50 border border-blue-100 flex items-start gap-2.5" data-testid="help-tip">
    <HelpCircle className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
    <div><p className="text-xs font-semibold text-blue-800">{title}</p><p className="text-[11px] text-blue-700 mt-0.5 leading-relaxed">{children}</p></div>
  </div>
);

const SectionHeader = ({ icon: Icon, title, desc }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><Icon className="w-5 h-5 text-primary" /></div>
    <div><h2 className="text-base font-bold">{title}</h2><p className="text-xs text-muted-foreground">{desc}</p></div>
  </div>
);

// ═══════════════════════════════════════════
// PROFILE & PREFERENCES
// ═══════════════════════════════════════════
const ProfileSection = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/user/profile").then(r => { setProfile(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try { await api.put("/user/profile", profile); toast.success("Profile updated"); }
    catch (e) { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  if (loading || !profile) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const prefs = profile.preferences || {};
  const updatePref = (k, v) => setProfile({ ...profile, preferences: { ...prefs, [k]: v } });

  return (
    <div className="space-y-5 max-w-3xl" data-testid="settings-profile">
      <SectionHeader icon={User} title="Profile & Preferences" desc="Manage your personal information and platform preferences" />
      <HelpTip title="About your profile">Your profile information is visible to administrators and used for communication. Keep your contact info up to date for important compliance and training notifications.</HelpTip>
      <Card className="border shadow-none">
        <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Personal Information</CardTitle></CardHeader>
        <CardContent className="px-5 pb-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label className="text-xs">Full Name</Label><Input value={profile.full_name || ""} onChange={e => setProfile({ ...profile, full_name: e.target.value })} className="h-9 text-sm" data-testid="profile-name" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input value={profile.email || ""} disabled className="h-9 text-sm bg-muted/50" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Job Title</Label><Input value={profile.title || ""} onChange={e => setProfile({ ...profile, title: e.target.value })} placeholder="e.g. Safety Manager" className="h-9 text-sm" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Phone</Label><Input value={profile.phone || ""} onChange={e => setProfile({ ...profile, phone: e.target.value })} placeholder="+1 (555) 000-0000" className="h-9 text-sm" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Bio</Label><Textarea value={profile.bio || ""} onChange={e => setProfile({ ...profile, bio: e.target.value })} rows={3} placeholder="Brief description about yourself..." className="text-sm" /></div>
        </CardContent>
      </Card>
      <Card className="border shadow-none">
        <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Preferences</CardTitle></CardHeader>
        <CardContent className="px-5 pb-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label className="text-xs">Timezone</Label>
              <Select value={prefs.timezone || "America/New_York"} onValueChange={v => updatePref("timezone", v)}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{["America/New_York","America/Chicago","America/Denver","America/Los_Angeles","America/Phoenix","Europe/London","Europe/Berlin","Asia/Tokyo","Asia/Singapore","Australia/Sydney"].map(tz => <SelectItem key={tz} value={tz} className="text-xs">{tz}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Language</Label>
              <Select value={prefs.language || "en"} onValueChange={v => updatePref("language", v)}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="en">English</SelectItem><SelectItem value="es">Spanish</SelectItem><SelectItem value="fr">French</SelectItem><SelectItem value="de">German</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Date Format</Label>
              <Select value={prefs.date_format || "MM/DD/YYYY"} onValueChange={v => updatePref("date_format", v)}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem><SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem><SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Email Digest</Label>
              <Select value={prefs.email_digest || "daily"} onValueChange={v => updatePref("email_digest", v)}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="realtime">Real-time</SelectItem><SelectItem value="daily">Daily</SelectItem><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="none">None</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
      <div className="flex justify-end"><Button onClick={handleSave} disabled={saving} className="gap-1.5" data-testid="save-profile-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Changes</Button></div>
    </div>
  );
};

// ═══════════════════════════════════════════
// ORGANIZATION PROFILE
// ═══════════════════════════════════════════
const AccountSection = () => {
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/organization/profile").then(r => { setOrg(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try { await api.put("/organization/profile", org); toast.success("Organization profile updated"); }
    catch (e) { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  if (loading || !org) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const addr = org.address || {};
  const setAddr = (k, v) => setOrg({ ...org, address: { ...addr, [k]: v } });

  return (
    <div className="space-y-5 max-w-3xl" data-testid="settings-account">
      <SectionHeader icon={Building2} title="Organization Profile" desc="Complete business profile for compliance reporting and platform configuration" />
      <HelpTip title="Why is this important?">Your organization profile is used for compliance reports, certificate generation, OSHA reporting, and regulatory filings. Complete and accurate information ensures compliance readiness.</HelpTip>
      <Card className="border shadow-none">
        <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Company Information</CardTitle></CardHeader>
        <CardContent className="px-5 pb-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label className="text-xs">Company Name *</Label><Input value={org.company_name || ""} onChange={e => setOrg({ ...org, company_name: e.target.value })} className="h-9 text-sm" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Legal Name</Label><Input value={org.legal_name || ""} onChange={e => setOrg({ ...org, legal_name: e.target.value })} placeholder="Full legal entity name" className="h-9 text-sm" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Industry *</Label>
              <Select value={org.industry || "manufacturing"} onValueChange={v => setOrg({ ...org, industry: v })}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{[{v:"manufacturing",l:"Manufacturing"},{v:"power_generation",l:"Power Generation"},{v:"water_treatment",l:"Water Treatment"},{v:"oil_gas",l:"Oil & Gas"},{v:"general",l:"General Industrial"},{v:"construction",l:"Construction"},{v:"chemical",l:"Chemical"},{v:"pharma",l:"Pharmaceutical"},{v:"food",l:"Food & Beverage"},{v:"automotive",l:"Automotive"}].map(i => <SelectItem key={i.v} value={i.v} className="text-xs">{i.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Company Size</Label>
              <Select value={org.company_size || "51-200"} onValueChange={v => setOrg({ ...org, company_size: v })}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{["1-10","11-50","51-200","201-500","501-1000","1001-5000","5000+"].map(s => <SelectItem key={s} value={s} className="text-xs">{s} employees</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Website</Label><Input value={org.website || ""} onChange={e => setOrg({ ...org, website: e.target.value })} placeholder="https://" className="h-9 text-sm" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Founded Year</Label><Input value={org.founded_year || ""} onChange={e => setOrg({ ...org, founded_year: e.target.value })} placeholder="e.g. 1995" className="h-9 text-sm" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Description</Label><Textarea value={org.description || ""} onChange={e => setOrg({ ...org, description: e.target.value })} rows={2} placeholder="Brief company description..." className="text-sm" /></div>
        </CardContent>
      </Card>
      <Card className="border shadow-none">
        <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Address & Contact</CardTitle></CardHeader>
        <CardContent className="px-5 pb-5 space-y-4">
          <div className="space-y-1.5"><Label className="text-xs">Street Address</Label><Input value={addr.street || ""} onChange={e => setAddr("street", e.target.value)} className="h-9 text-sm" /></div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">City</Label><Input value={addr.city || ""} onChange={e => setAddr("city", e.target.value)} className="h-9 text-sm" /></div>
            <div className="space-y-1.5"><Label className="text-xs">State</Label><Input value={addr.state || ""} onChange={e => setAddr("state", e.target.value)} className="h-9 text-sm" /></div>
            <div className="space-y-1.5"><Label className="text-xs">ZIP</Label><Input value={addr.zip || ""} onChange={e => setAddr("zip", e.target.value)} className="h-9 text-sm" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Country</Label><Input value={addr.country || "US"} onChange={e => setAddr("country", e.target.value)} className="h-9 text-sm" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label className="text-xs">Main Phone</Label><Input value={org.phone || ""} onChange={e => setOrg({ ...org, phone: e.target.value })} className="h-9 text-sm" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Main Email</Label><Input value={org.email || ""} onChange={e => setOrg({ ...org, email: e.target.value })} className="h-9 text-sm" /></div>
          </div>
        </CardContent>
      </Card>
      <Card className="border shadow-none">
        <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Regulatory & Compliance</CardTitle></CardHeader>
        <CardContent className="px-5 pb-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label className="text-xs">Tax ID / EIN</Label><Input value={org.tax_id || ""} onChange={e => setOrg({ ...org, tax_id: e.target.value })} placeholder="XX-XXXXXXX" className="h-9 text-sm" /></div>
            <div className="space-y-1.5"><Label className="text-xs">NAICS Code</Label><Input value={org.naics_code || ""} onChange={e => setOrg({ ...org, naics_code: e.target.value })} placeholder="e.g. 332710" className="h-9 text-sm" /></div>
            <div className="space-y-1.5"><Label className="text-xs">SIC Code</Label><Input value={org.sic_code || ""} onChange={e => setOrg({ ...org, sic_code: e.target.value })} placeholder="e.g. 3599" className="h-9 text-sm" /></div>
            <div className="space-y-1.5"><Label className="text-xs">OSHA Establishment Size</Label>
              <Select value={org.osha_establishment_size || ""} onValueChange={v => setOrg({ ...org, osha_establishment_size: v })}>
                <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Select size" /></SelectTrigger>
                <SelectContent><SelectItem value="1-19">1-19</SelectItem><SelectItem value="20-249">20-249</SelectItem><SelectItem value="250+">250+</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg border">
            <div><p className="text-sm font-medium">Union Facility</p><p className="text-[10px] text-muted-foreground">Does this facility have union representation?</p></div>
            <Switch checked={org.union_facility || false} onCheckedChange={v => setOrg({ ...org, union_facility: v })} />
          </div>
        </CardContent>
      </Card>
      <Card className="border shadow-none">
        <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Operational Details</CardTitle></CardHeader>
        <CardContent className="px-5 pb-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label className="text-xs">Employee Count</Label><Input type="number" value={org.employee_count || 0} onChange={e => setOrg({ ...org, employee_count: parseInt(e.target.value) || 0 })} className="h-9 text-sm" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Contractor Count</Label><Input type="number" value={org.contractor_count || 0} onChange={e => setOrg({ ...org, contractor_count: parseInt(e.target.value) || 0 })} className="h-9 text-sm" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Facility Count</Label><Input type="number" value={org.facility_count || 1} onChange={e => setOrg({ ...org, facility_count: parseInt(e.target.value) || 1 })} className="h-9 text-sm" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Shifts per Day</Label><Input type="number" value={org.shift_count || 1} onChange={e => setOrg({ ...org, shift_count: parseInt(e.target.value) || 1 })} className="h-9 text-sm" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Annual Training Budget ($)</Label><Input type="number" value={org.training_budget_annual || 0} onChange={e => setOrg({ ...org, training_budget_annual: parseInt(e.target.value) || 0 })} className="h-9 text-sm" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Fiscal Year Start</Label>
              <Select value={org.fiscal_year_start || "January"} onValueChange={v => setOrg({ ...org, fiscal_year_start: v })}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{["January","February","March","April","May","June","July","August","September","October","November","December"].map(m => <SelectItem key={m} value={m} className="text-xs">{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card className="border shadow-none">
        <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Key Contacts</CardTitle></CardHeader>
        <CardContent className="px-5 pb-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label className="text-xs">Safety Officer</Label><Input value={org.safety_officer || ""} onChange={e => setOrg({ ...org, safety_officer: e.target.value })} className="h-9 text-sm" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Safety Officer Email</Label><Input value={org.safety_officer_email || ""} onChange={e => setOrg({ ...org, safety_officer_email: e.target.value })} className="h-9 text-sm" /></div>
            <div className="space-y-1.5"><Label className="text-xs">HR Contact</Label><Input value={org.hr_contact || ""} onChange={e => setOrg({ ...org, hr_contact: e.target.value })} className="h-9 text-sm" /></div>
            <div className="space-y-1.5"><Label className="text-xs">HR Email</Label><Input value={org.hr_email || ""} onChange={e => setOrg({ ...org, hr_email: e.target.value })} className="h-9 text-sm" /></div>
            <div className="space-y-1.5"><Label className="text-xs">IT Contact</Label><Input value={org.it_contact || ""} onChange={e => setOrg({ ...org, it_contact: e.target.value })} className="h-9 text-sm" /></div>
            <div className="space-y-1.5"><Label className="text-xs">IT Email</Label><Input value={org.it_email || ""} onChange={e => setOrg({ ...org, it_email: e.target.value })} className="h-9 text-sm" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Training Manager</Label><Input value={org.training_manager || ""} onChange={e => setOrg({ ...org, training_manager: e.target.value })} className="h-9 text-sm" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Training Manager Email</Label><Input value={org.training_manager_email || ""} onChange={e => setOrg({ ...org, training_manager_email: e.target.value })} className="h-9 text-sm" /></div>
          </div>
        </CardContent>
      </Card>
      <div className="flex justify-end"><Button onClick={handleSave} disabled={saving} className="gap-1.5" data-testid="save-org-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Organization Profile</Button></div>
    </div>
  );
};

// ═══════════════════════════════════════════
// SECURITY SETTINGS
// ═══════════════════════════════════════════
const SecuritySection = () => {
  const [passwords, setPasswords] = useState({ current: "", new_password: "", confirm: "" });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [securitySettings, setSecuritySettings] = useState({
    two_factor: false, session_timeout: 480, password_expiry_days: 90,
    enforce_strong_password: true, max_login_attempts: 5, ip_whitelist_enabled: false, ip_whitelist: ""
  });

  const handleChangePassword = async () => {
    if (passwords.new_password !== passwords.confirm) { toast.error("Passwords do not match"); return; }
    if (passwords.new_password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setSaving(true);
    try {
      await api.put("/user/change-password", { current_password: passwords.current, new_password: passwords.new_password });
      toast.success("Password updated");
      setPasswords({ current: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to change password"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 max-w-3xl" data-testid="settings-security">
      <SectionHeader icon={Lock} title="Security Settings" desc="Password management, two-factor authentication, and session controls" />
      <HelpTip title="Security Best Practices">Use strong, unique passwords and enable two-factor authentication (2FA) for maximum account security. For compliance (NIST 800-63B), passwords should be at least 12 characters with mixed complexity.</HelpTip>
      <Card className="border shadow-none">
        <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Change Password</CardTitle></CardHeader>
        <CardContent className="px-5 pb-5 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Current Password</Label>
            <div className="relative">
              <Input type={showCurrent ? "text" : "password"} value={passwords.current} onChange={e => setPasswords({ ...passwords, current: e.target.value })} className="h-9 text-sm pr-10" data-testid="current-password" />
              <button onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">{showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">New Password</Label>
              <div className="relative">
                <Input type={showNew ? "text" : "password"} value={passwords.new_password} onChange={e => setPasswords({ ...passwords, new_password: e.target.value })} className="h-9 text-sm pr-10" data-testid="new-password" />
                <button onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">{showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
              </div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Confirm New Password</Label><Input type="password" value={passwords.confirm} onChange={e => setPasswords({ ...passwords, confirm: e.target.value })} className="h-9 text-sm" data-testid="confirm-password" /></div>
          </div>
          <Button onClick={handleChangePassword} disabled={saving || !passwords.current || !passwords.new_password} className="gap-1.5" data-testid="change-password-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />} Update Password
          </Button>
        </CardContent>
      </Card>
      <Card className="border shadow-none">
        <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Authentication & Sessions</CardTitle></CardHeader>
        <CardContent className="px-5 pb-5 space-y-3">
          <div className="flex items-center justify-between p-3 rounded-lg border">
            <div><p className="text-sm font-medium">Two-Factor Authentication (2FA)</p><p className="text-[10px] text-muted-foreground">Add an extra layer of security to your account</p></div>
            <Switch checked={securitySettings.two_factor} onCheckedChange={v => { setSecuritySettings({ ...securitySettings, two_factor: v }); toast.info("2FA configuration coming soon"); }} />
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg border">
            <div><p className="text-sm font-medium">Enforce Strong Passwords</p><p className="text-[10px] text-muted-foreground">Require uppercase, lowercase, number, and special character</p></div>
            <Switch checked={securitySettings.enforce_strong_password} onCheckedChange={v => setSecuritySettings({ ...securitySettings, enforce_strong_password: v })} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
            <div className="space-y-1.5"><Label className="text-xs">Session Timeout (minutes)</Label><Input type="number" value={securitySettings.session_timeout} onChange={e => setSecuritySettings({ ...securitySettings, session_timeout: parseInt(e.target.value) || 480 })} className="h-9 text-sm" /><p className="text-[10px] text-muted-foreground">Auto-logout after inactivity (recommended: 480 min for enterprise)</p></div>
            <div className="space-y-1.5"><Label className="text-xs">Password Expiry (days)</Label><Input type="number" value={securitySettings.password_expiry_days} onChange={e => setSecuritySettings({ ...securitySettings, password_expiry_days: parseInt(e.target.value) || 90 })} className="h-9 text-sm" /><p className="text-[10px] text-muted-foreground">Force password change every N days (0 = never)</p></div>
            <div className="space-y-1.5"><Label className="text-xs">Max Login Attempts</Label><Input type="number" value={securitySettings.max_login_attempts} onChange={e => setSecuritySettings({ ...securitySettings, max_login_attempts: parseInt(e.target.value) || 5 })} className="h-9 text-sm" /><p className="text-[10px] text-muted-foreground">Lock account after N failed attempts</p></div>
          </div>
        </CardContent>
      </Card>
      <Card className="border shadow-none">
        <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Active Sessions</CardTitle></CardHeader>
        <CardContent className="px-5 pb-5 space-y-2">
          {[
            { device: "Chrome on Windows", location: "New York, US", current: true, last: "Just now" },
            { device: "Safari on macOS", location: "San Francisco, US", current: false, last: "2 hours ago" },
          ].map((session, i) => (
            <div key={i} className="flex items-center justify-between p-3 rounded-lg border">
              <div className="flex items-center gap-3">
                <Monitor className="w-5 h-5 text-muted-foreground" />
                <div>
                  <span className="text-sm font-medium">{session.device} {session.current && <Badge className="ml-1 text-[9px] bg-green-50 text-green-700 border-green-200">Current</Badge>}</span>
                  <p className="text-[10px] text-muted-foreground">{session.location} &middot; {session.last}</p>
                </div>
              </div>
              {!session.current && <Button variant="ghost" size="sm" className="text-xs text-red-600 hover:text-red-700 h-7">Revoke</Button>}
            </div>
          ))}
        </CardContent>
      </Card>
      <div className="flex justify-end"><Button onClick={() => toast.success("Security settings saved")} className="gap-1.5"><Save className="w-4 h-4" /> Save Security Settings</Button></div>
    </div>
  );
};

// ═══════════════════════════════════════════
// NOTIFICATIONS SETTINGS
// ═══════════════════════════════════════════
const NotificationsSection = () => {
  const [settings, setSettings] = useState({
    email_compliance_due: true, email_course_assigned: true, email_assessment_due: true,
    email_certificate_earned: true, email_system_alerts: true, email_weekly_digest: true,
    push_enabled: false, push_compliance_due: true, push_course_assigned: true,
    quiet_hours_enabled: true, quiet_hours_start: "22:00", quiet_hours_end: "07:00",
  });
  const toggle = (key) => setSettings(p => ({ ...p, [key]: !p[key] }));

  return (
    <div className="space-y-5 max-w-3xl" data-testid="settings-notifications">
      <SectionHeader icon={Bell} title="Notification Preferences" desc="Control how and when you receive notifications" />
      <HelpTip title="Stay Informed">Configure notifications to stay on top of compliance deadlines, new course assignments, and important system alerts. Quiet hours ensure you're not disturbed during off-hours.</HelpTip>
      <Card className="border shadow-none">
        <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Email Notifications</CardTitle></CardHeader>
        <CardContent className="px-5 pb-5 space-y-2">
          {[
            { key: "email_compliance_due", label: "Compliance Deadlines", desc: "Get notified when compliance items are approaching due dates" },
            { key: "email_course_assigned", label: "Course Assignments", desc: "Notification when new courses are assigned to you" },
            { key: "email_assessment_due", label: "Assessment Reminders", desc: "Reminders for upcoming assessments and quizzes" },
            { key: "email_certificate_earned", label: "Certificate Notifications", desc: "Receive certificate when training is completed" },
            { key: "email_system_alerts", label: "System Alerts", desc: "Critical system notifications and maintenance updates" },
            { key: "email_weekly_digest", label: "Weekly Digest", desc: "Summary of training activity and upcoming deadlines" },
          ].map(item => (
            <div key={item.key} className="flex items-center justify-between p-3 rounded-lg border">
              <div><p className="text-sm font-medium">{item.label}</p><p className="text-[10px] text-muted-foreground">{item.desc}</p></div>
              <Switch checked={settings[item.key]} onCheckedChange={() => toggle(item.key)} />
            </div>
          ))}
        </CardContent>
      </Card>
      <Card className="border shadow-none">
        <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Quiet Hours</CardTitle></CardHeader>
        <CardContent className="px-5 pb-5 space-y-3">
          <div className="flex items-center justify-between p-3 rounded-lg border">
            <div><p className="text-sm font-medium">Enable Quiet Hours</p><p className="text-[10px] text-muted-foreground">Suppress non-critical notifications during specified hours</p></div>
            <Switch checked={settings.quiet_hours_enabled} onCheckedChange={() => toggle("quiet_hours_enabled")} />
          </div>
          {settings.quiet_hours_enabled && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs">Start Time</Label><Input type="time" value={settings.quiet_hours_start} onChange={e => setSettings({ ...settings, quiet_hours_start: e.target.value })} className="h-9 text-sm" /></div>
              <div className="space-y-1.5"><Label className="text-xs">End Time</Label><Input type="time" value={settings.quiet_hours_end} onChange={e => setSettings({ ...settings, quiet_hours_end: e.target.value })} className="h-9 text-sm" /></div>
            </div>
          )}
        </CardContent>
      </Card>
      <div className="flex justify-end"><Button onClick={() => toast.success("Notification preferences saved")} className="gap-1.5"><Save className="w-4 h-4" /> Save Preferences</Button></div>
    </div>
  );
};

// ═══════════════════════════════════════════
// DEPARTMENTS (with wizard-style create)
// ═══════════════════════════════════════════
const DepartmentsSection = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newDept, setNewDept] = useState({ name: "", description: "", head: "" });
  const [editDept, setEditDept] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchDepts = async () => {
    try { const r = await api.get("/departments"); setDepartments(r.data || []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchDepts(); }, []);

  const handleCreate = async () => {
    if (!newDept.name.trim()) { toast.error("Department name is required"); return; }
    setSaving(true);
    try {
      await api.post("/departments", newDept);
      toast.success(`Department "${newDept.name}" created`);
      setNewDept({ name: "", description: "", head: "" });
      setShowCreate(false);
      fetchDepts();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create"); }
    finally { setSaving(false); }
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      await api.put(`/departments/${editDept.originalName}`, editDept);
      toast.success("Department updated");
      setEditDept(null);
      fetchDepts();
    } catch (e) { toast.error("Failed to update"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (name) => {
    if (!window.confirm(`Delete department "${name}"? Members will become unassigned.`)) return;
    try {
      await api.delete(`/departments/${name}`);
      toast.success("Department deleted");
      fetchDepts();
    } catch (e) { toast.error("Failed to delete"); }
  };

  return (
    <div className="space-y-5 max-w-4xl" data-testid="settings-departments">
      <SectionHeader icon={Users} title="Departments" desc="Manage organizational structure and department hierarchy" />
      <HelpTip title="Department Management">Departments help organize your team for compliance tracking, reporting, and auto-assignment of training. Each department can have a designated head for escalation workflows.</HelpTip>

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{departments.length} departments configured</p>
        <Button onClick={() => setShowCreate(true)} className="gap-1.5 text-xs h-8" data-testid="add-dept-btn"><Plus className="w-3.5 h-3.5" /> Add Department</Button>
      </div>

      {/* Create Wizard */}
      {showCreate && (
        <Card className="border-2 border-primary/30 shadow-none">
          <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Create New Department</CardTitle></CardHeader>
          <CardContent className="px-5 pb-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs">Department Name *</Label><Input value={newDept.name} onChange={e => setNewDept({ ...newDept, name: e.target.value })} placeholder="e.g. Quality Assurance" className="h-9 text-sm" data-testid="dept-name-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Department Head</Label><Input value={newDept.head} onChange={e => setNewDept({ ...newDept, head: e.target.value })} placeholder="e.g. John Smith" className="h-9 text-sm" /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Description</Label><Textarea value={newDept.description} onChange={e => setNewDept({ ...newDept, description: e.target.value })} rows={2} placeholder="Brief department description..." className="text-sm" /></div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" className="text-xs h-8" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button size="sm" className="text-xs h-8 gap-1" onClick={handleCreate} disabled={saving} data-testid="create-dept-btn">{saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Create</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Modal */}
      {editDept && (
        <Card className="border-2 border-amber-300 shadow-none">
          <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Edit Department: {editDept.originalName}</CardTitle></CardHeader>
          <CardContent className="px-5 pb-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs">Department Name</Label><Input value={editDept.name} onChange={e => setEditDept({ ...editDept, name: e.target.value })} className="h-9 text-sm" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Department Head</Label><Input value={editDept.head || ""} onChange={e => setEditDept({ ...editDept, head: e.target.value })} className="h-9 text-sm" /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Description</Label><Textarea value={editDept.description || ""} onChange={e => setEditDept({ ...editDept, description: e.target.value })} rows={2} className="text-sm" /></div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" className="text-xs h-8" onClick={() => setEditDept(null)}>Cancel</Button>
              <Button size="sm" className="text-xs h-8 gap-1" onClick={handleUpdate} disabled={saving}>{saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
        <div className="space-y-2">
          {departments.map((dept, i) => (
            <Card key={i} className="border shadow-none">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center"><Users className="w-5 h-5 text-teal-600" /></div>
                  <div>
                    <p className="text-sm font-semibold">{dept.name}</p>
                    <p className="text-[10px] text-muted-foreground">{dept.description || "No description"} {dept.head && `· Head: ${dept.head}`}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="text-xs">{dept.member_count} members</Badge>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setEditDept({ ...dept, originalName: dept.name })} data-testid={`edit-dept-${dept.name}`}><Pencil className="w-3 h-3" /></Button>
                  <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600 hover:text-red-700" onClick={() => handleDelete(dept.name)} data-testid={`delete-dept-${dept.name}`}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {departments.length === 0 && <p className="text-center text-sm text-muted-foreground py-8">No departments configured yet. Create your first department to organize your team.</p>}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════
// BRANDING SETTINGS
// ═══════════════════════════════════════════
const API_URL = process.env.REACT_APP_BACKEND_URL;

const LogoUploader = ({ label, desc, logoType, currentUrl, onUploaded }) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleUpload = async (file) => {
    if (!file) return;
    const validTypes = ["image/png", "image/jpeg", "image/svg+xml", "image/webp"];
    if (!validTypes.includes(file.type)) { toast.error("Please upload PNG, JPG, SVG, or WebP"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("File must be under 5MB"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("logo_type", logoType);
      const token = localStorage.getItem("access_token");
      const res = await fetch(`${API_URL}/api/branding/upload-logo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || "Upload failed"); }
      const data = await res.json();
      toast.success(data.message);
      onUploaded(data.url);
    } catch (e) { toast.error(e.message || "Upload failed"); }
    finally { setUploading(false); }
  };

  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files[0]) handleUpload(e.dataTransfer.files[0]); };

  return (
    <div className="p-4 rounded-lg border space-y-3" data-testid={`logo-uploader-${logoType}`}>
      <div className="flex items-center justify-between">
        <div><p className="text-sm font-medium">{label}</p><p className="text-[10px] text-muted-foreground">{desc}</p></div>
      </div>
      <div className="flex items-start gap-4">
        {/* Preview */}
        <div className="w-28 h-20 rounded-lg border-2 border-dashed bg-muted/20 flex items-center justify-center shrink-0 overflow-hidden">
          {currentUrl ? (
            <img src={`${API_URL}${currentUrl}`} alt={`${logoType} logo`} className="max-w-full max-h-full object-contain p-1" />
          ) : (
            <div className="text-center"><Image className="w-5 h-5 mx-auto text-muted-foreground/40" /><p className="text-[9px] text-muted-foreground/50 mt-1">No logo</p></div>
          )}
        </div>
        {/* Upload zone */}
        <div
          className={`flex-1 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${dragOver ? "border-primary bg-primary/5" : "border-muted-foreground/20 hover:border-primary/40"}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => { const input = document.createElement("input"); input.type = "file"; input.accept = "image/png,image/jpeg,image/svg+xml,image/webp"; input.onchange = (e) => handleUpload(e.target.files[0]); input.click(); }}
        >
          {uploading ? (
            <div className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin text-primary" /><span className="text-xs text-primary">Uploading...</span></div>
          ) : (
            <>
              <Upload className="w-5 h-5 mx-auto text-muted-foreground/40 mb-1" />
              <p className="text-xs text-muted-foreground">Drag & drop or <span className="text-primary font-medium">browse</span></p>
              <p className="text-[10px] text-muted-foreground/60 mt-0.5">PNG, JPG, SVG or WebP (max 5MB)</p>
            </>
          )}
        </div>
      </div>
      {currentUrl && (
        <Button variant="ghost" size="sm" className="text-xs h-7 text-red-600 hover:text-red-700 gap-1" onClick={() => onUploaded("")} data-testid={`remove-logo-${logoType}`}>
          <Trash2 className="w-3 h-3" /> Remove Logo
        </Button>
      )}
    </div>
  );
};

const BrandingSection = () => {
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { refreshBranding } = useBranding();

  useEffect(() => {
    api.get("/branding/settings").then(r => { setBranding(r.data); setLoading(false); }).catch(() => {
      setBranding({ primary_color: "#0F1923", accent_color: "#1B4F8A", certificate_logo: true, email_logo: true, login_message: "", support_url: "", certificate_logo_url: "", email_logo_url: "", main_logo_url: "" });
      setLoading(false);
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try { await api.put("/branding/settings", branding); toast.success("Branding settings saved"); refreshBranding(); }
    catch (e) { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  if (loading || !branding) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-5 max-w-3xl" data-testid="settings-branding">
      <SectionHeader icon={Palette} title="Branding & Appearance" desc="Customize the look and feel of your LMS portal" />
      <HelpTip title="Brand Consistency">Configure your brand colors, logos, and custom text to match your corporate identity. Changes apply across login pages, certificates, email templates, and the main dashboard.</HelpTip>
      <Card className="border shadow-none">
        <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Color Scheme</CardTitle></CardHeader>
        <CardContent className="px-5 pb-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Primary Color</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={branding.primary_color} onChange={e => setBranding({ ...branding, primary_color: e.target.value })} className="w-9 h-9 rounded border cursor-pointer" />
                <Input value={branding.primary_color} onChange={e => setBranding({ ...branding, primary_color: e.target.value })} className="h-9 text-sm flex-1" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Accent Color</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={branding.accent_color} onChange={e => setBranding({ ...branding, accent_color: e.target.value })} className="w-9 h-9 rounded border cursor-pointer" />
                <Input value={branding.accent_color} onChange={e => setBranding({ ...branding, accent_color: e.target.value })} className="h-9 text-sm flex-1" />
              </div>
            </div>
          </div>
          <div className="p-4 rounded-lg border bg-muted/20">
            <p className="text-xs font-medium mb-2">Preview</p>
            <div className="flex gap-3">
              <div className="w-24 h-12 rounded-lg flex items-center justify-center text-white text-xs font-semibold" style={{ backgroundColor: branding.primary_color }}>Primary</div>
              <div className="w-24 h-12 rounded-lg flex items-center justify-center text-white text-xs font-semibold" style={{ backgroundColor: branding.accent_color }}>Accent</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border shadow-none">
        <CardHeader className="pt-4 pb-2 px-5">
          <CardTitle className="text-sm">Logo Management</CardTitle>
          <p className="text-[10px] text-muted-foreground mt-0.5">Upload logos for different contexts. Recommended: transparent PNG, at least 400px wide.</p>
        </CardHeader>
        <CardContent className="px-5 pb-5 space-y-4">
          <LogoUploader
            label="Main / Portal Logo"
            desc="Displayed on the dashboard sidebar and login page"
            logoType="main"
            currentUrl={branding.main_logo_url}
            onUploaded={(url) => setBranding({ ...branding, main_logo_url: url })}
          />
          <LogoUploader
            label="Certificate Logo"
            desc="Printed on training completion certificates"
            logoType="certificate"
            currentUrl={branding.certificate_logo_url}
            onUploaded={(url) => setBranding({ ...branding, certificate_logo_url: url })}
          />
          <LogoUploader
            label="Email Logo"
            desc="Included in notification emails and digests"
            logoType="email"
            currentUrl={branding.email_logo_url}
            onUploaded={(url) => setBranding({ ...branding, email_logo_url: url })}
          />
        </CardContent>
      </Card>

      <Card className="border shadow-none">
        <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Logo Display Options</CardTitle></CardHeader>
        <CardContent className="px-5 pb-5 space-y-3">
          <div className="flex items-center justify-between p-3 rounded-lg border">
            <div><p className="text-sm font-medium">Show Logo on Certificates</p><p className="text-[10px] text-muted-foreground">Include your uploaded certificate logo on generated certificates</p></div>
            <Switch checked={branding.certificate_logo} onCheckedChange={v => setBranding({ ...branding, certificate_logo: v })} />
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg border">
            <div><p className="text-sm font-medium">Show Logo in Emails</p><p className="text-[10px] text-muted-foreground">Include your uploaded email logo in notification emails</p></div>
            <Switch checked={branding.email_logo} onCheckedChange={v => setBranding({ ...branding, email_logo: v })} />
          </div>
        </CardContent>
      </Card>

      <Card className="border shadow-none">
        <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Custom Text</CardTitle></CardHeader>
        <CardContent className="px-5 pb-5 space-y-4">
          <div className="space-y-1.5"><Label className="text-xs">Login Page Message</Label><Textarea value={branding.login_message} onChange={e => setBranding({ ...branding, login_message: e.target.value })} rows={2} placeholder="Welcome message shown on the login page..." className="text-sm" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Support URL</Label><Input value={branding.support_url} onChange={e => setBranding({ ...branding, support_url: e.target.value })} placeholder="https://support.yourcompany.com" className="h-9 text-sm" /></div>
        </CardContent>
      </Card>
      <div className="flex justify-end"><Button onClick={handleSave} disabled={saving} className="gap-1.5" data-testid="save-branding-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Branding</Button></div>
    </div>
  );
};

// ═══════════════════════════════════════════
// INTEGRATIONS
// ═══════════════════════════════════════════
const IntegrationsSection = () => {
  const integrations = [
    { name: "Microsoft 365", desc: "Connect to M365 for calendar sync, Teams meetings, and user provisioning", logo: "/logo-microsoft.png", status: "not_connected", category: "Productivity" },
    { name: "Google Workspace", desc: "Google Calendar, Google Meet, and user directory sync", logo: "/logo-google.webp", status: "not_connected", category: "Productivity" },
    { name: "SCIM Provisioning", desc: "Automatic user provisioning and de-provisioning via SCIM 2.0", icon: Users, status: "not_connected", category: "Identity" },
    { name: "SMTP/Email", desc: "Custom SMTP server for sending notifications and alerts", icon: Mail, status: "not_connected", category: "Communication" },
    { name: "Slack", desc: "Send training notifications and reminders to Slack channels", logo: "/logo-slack.png", status: "not_connected", category: "Communication" },
    { name: "API Access", desc: "RESTful API for custom integrations and data export", icon: Database, status: "available", category: "Developer" },
    { name: "Webhooks", desc: "Real-time event notifications to external systems", icon: Webhook, status: "not_connected", category: "Developer" },
    { name: "SAML SSO", desc: "Enterprise single sign-on via SAML 2.0", icon: Shield, status: "not_connected", category: "Identity" },
  ];

  const categories = [...new Set(integrations.map(i => i.category))];

  return (
    <div className="space-y-5 max-w-4xl" data-testid="settings-integrations">
      <SectionHeader icon={Link2} title="Integrations" desc="Connect external services and configure API access" />
      <HelpTip title="Enterprise Integrations">Integrate with your existing tech stack for seamless user provisioning, calendar sync, and automated notifications. SAML SSO and SCIM are available on Enterprise plans.</HelpTip>
      {categories.map(cat => (
        <div key={cat}>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">{cat}</h3>
          <div className="space-y-2 mb-5">
            {integrations.filter(i => i.category === cat).map((integ, j) => (
              <Card key={j} className="border shadow-none">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-muted/50 flex items-center justify-center overflow-hidden">
                      {integ.logo ? <img src={integ.logo} alt={integ.name} className="w-6 h-6 object-contain" /> : <integ.icon className="w-5 h-5 text-muted-foreground" />}
                    </div>
                    <div>
                      <p className="text-sm font-semibold">{integ.name}</p>
                      <p className="text-[10px] text-muted-foreground">{integ.desc}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`text-[10px] ${integ.status === "connected" ? "text-green-600 border-green-200" : integ.status === "available" ? "text-blue-600 border-blue-200" : "text-muted-foreground"}`}>
                      {integ.status === "connected" ? "Connected" : integ.status === "available" ? "Available" : "Not Connected"}
                    </Badge>
                    <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => toast.info(`${integ.name} integration coming soon`)} data-testid={`connect-${integ.name.toLowerCase().replace(/\s/g,'-')}`}>
                      {integ.status === "connected" ? "Configure" : "Connect"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════
// ROLES & ACCESS (RBAC)
// ═══════════════════════════════════════════
const RBACSection = () => {
  const [roles, setRoles] = useState([]);
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingRole, setEditingRole] = useState(null);
  const [editPerms, setEditPerms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newRole, setNewRole] = useState({ name: "", description: "", permissions: [] });
  const [creating, setCreating] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [rolesRes, schemaRes] = await Promise.all([
        api.get("/rbac/roles?scope=platform"),
        api.get("/rbac/permissions-schema"),
      ]);
      setRoles(rolesRes.data?.roles || []);
      setSchema(schemaRes.data);
    } catch (e) {
      toast.error("Failed to load RBAC data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const startEdit = (role) => {
    setEditingRole(role);
    setEditPerms([...role.permissions]);
  };

  const togglePerm = (perm, permsState, setPermsState) => {
    setPermsState(prev => prev.includes(perm) ? prev.filter(p => p !== perm) : [...prev, perm]);
  };

  const savePermissions = async () => {
    if (!editingRole) return;
    setSaving(true);
    try {
      await api.put(`/rbac/roles/${editingRole.id}`, { permissions: editPerms });
      toast.success(`Permissions saved for ${editingRole.name}`);
      setEditingRole(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!newRole.name.trim()) { toast.error("Role name is required"); return; }
    setCreating(true);
    try {
      await api.post("/rbac/roles", newRole);
      toast.success(`Role "${newRole.name}" created`);
      setShowCreate(false);
      setNewRole({ name: "", description: "", permissions: [] });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create role");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (role) => {
    if (!window.confirm(`Delete "${role.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/rbac/roles/${role.id}`);
      toast.success(`Role "${role.name}" deleted`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset all roles to default permissions?")) return;
    try {
      await api.post("/rbac/reset");
      toast.success("RBAC reset to defaults");
      fetchData();
    } catch (e) {
      toast.error("Failed to reset");
    }
  };

  const modules = schema?.modules || {};
  const ROLE_CARD_COLORS = [
    { bg: "bg-white", border: "border-slate-200", text: "text-slate-800", icon: "bg-[#1e3a5f]/10 text-[#1e3a5f]", accent: "bg-[#1e3a5f]" },
    { bg: "bg-white", border: "border-slate-200", text: "text-blue-700", icon: "bg-blue-50 text-blue-600", accent: "bg-blue-500" },
    { bg: "bg-white", border: "border-slate-200", text: "text-emerald-700", icon: "bg-emerald-50 text-emerald-600", accent: "bg-emerald-500" },
    { bg: "bg-white", border: "border-slate-200", text: "text-amber-700", icon: "bg-amber-50 text-amber-600", accent: "bg-amber-500" },
    { bg: "bg-white", border: "border-slate-200", text: "text-violet-700", icon: "bg-violet-50 text-violet-600", accent: "bg-violet-500" },
    { bg: "bg-white", border: "border-slate-200", text: "text-indigo-700", icon: "bg-indigo-50 text-indigo-600", accent: "bg-indigo-500" },
    { bg: "bg-white", border: "border-slate-200", text: "text-teal-700", icon: "bg-teal-50 text-teal-600", accent: "bg-teal-500" },
    { bg: "bg-white", border: "border-slate-200", text: "text-orange-700", icon: "bg-orange-50 text-orange-600", accent: "bg-orange-500" },
  ];
  const getColor = (i) => ROLE_CARD_COLORS[i % ROLE_CARD_COLORS.length];
  const COMPACT_COLORS = [
    { bg: "bg-[#1e3a5f]", text: "text-white", accent: "border-[#1e3a5f]", light: "bg-[#1e3a5f]/5 text-[#1e3a5f]" },
    { bg: "bg-blue-500", text: "text-white", accent: "border-blue-500", light: "bg-blue-50 text-blue-600" },
    { bg: "bg-emerald-500", text: "text-white", accent: "border-emerald-500", light: "bg-emerald-50 text-emerald-600" },
    { bg: "bg-amber-500", text: "text-white", accent: "border-amber-500", light: "bg-amber-50 text-amber-700" },
    { bg: "bg-violet-500", text: "text-white", accent: "border-violet-500", light: "bg-violet-50 text-violet-600" },
    { bg: "bg-rose-500", text: "text-white", accent: "border-rose-500", light: "bg-rose-50 text-rose-600" },
    { bg: "bg-teal-500", text: "text-white", accent: "border-teal-500", light: "bg-teal-50 text-teal-600" },
    { bg: "bg-orange-500", text: "text-white", accent: "border-orange-500", light: "bg-orange-50 text-orange-600" },
    { bg: "bg-indigo-500", text: "text-white", accent: "border-indigo-500", light: "bg-indigo-50 text-indigo-600" },
    { bg: "bg-cyan-500", text: "text-white", accent: "border-cyan-500", light: "bg-cyan-50 text-cyan-600" },
  ];

  const RolesView = ({ roles: rv, getColor: gc, editingRole: er, setEditingRole: ser, startEdit: se, editPerms: ep, setEditPerms: sep, savePermissions: sp, saving: sv, handleDelete: hd, handleReset: hr }) => {
    const navigate = useNavigate();
    const [expandedRole, setExpandedRole] = useState(null);
    const [roleStaff, setRoleStaff] = useState([]);
    const [loadingStaff, setLoadingStaff] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState("");

    const fetchRoleStaff = async (role) => {
      setLoadingStaff(true);
      try {
        const res = await api.get(`/admin/platform-staff`);
        const staff = (res.data?.staff || []).filter(s =>
          role.id === "super_admin" ? (s.role === "super_admin" && !s.custom_role) : s.custom_role === role.id
        );
        setRoleStaff(staff);
      } catch { setRoleStaff([]); }
      finally { setLoadingStaff(false); }
    };

    const toggleExpand = (role) => {
      if (expandedRole?.id === role.id) {
        setExpandedRole(null);
        setRoleStaff([]);
      } else {
        setExpandedRole(role);
        fetchRoleStaff(role);
      }
    };

    const confirmDelete = () => {
      if (!deleteTarget) return;
      hd(deleteTarget);
      setDeleteTarget(null);
      setDeleteConfirmText("");
    };

    return (
      <>
        {/* Compact Role Cards Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="role-cards-grid">
          {rv.map((role, i) => {
            const cc = COMPACT_COLORS[i % COMPACT_COLORS.length];
            const isExpanded = expandedRole?.id === role.id;
            const isEditing = er?.id === role.id;
            return (
              <div key={role.id}
                className={cn("relative rounded-xl border bg-white overflow-hidden cursor-pointer transition-all hover:shadow-md",
                  isExpanded && "ring-2 ring-offset-1", cc.accent, isEditing && "ring-2 ring-[#1e3a5f]"
                )}
                onClick={() => toggleExpand(role)}
                data-testid={`role-card-${role.id}`}>
                <div className={cn("h-1.5 w-full", cc.bg)} />
                <div className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className={cn("w-7 h-7 rounded-md flex items-center justify-center text-xs font-bold", cc.bg, cc.text)}>
                      {role.user_count}
                    </div>
                    {role.is_system ? (
                      <Badge variant="outline" className="text-[8px] px-1 py-0">System</Badge>
                    ) : (
                      <button className="h-6 w-6 rounded-md flex items-center justify-center text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(role); setDeleteConfirmText(""); }}
                        data-testid={`delete-role-${role.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  <p className="text-xs font-semibold text-foreground truncate" title={role.name}>{role.name}</p>
                  <p className="text-[10px] text-muted-foreground">{role.permission_count} perms</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Expanded Role Panel - Staff List */}
        {expandedRole && (
          <Card className="border shadow-sm animate-in slide-in-from-top-2 duration-200">
            <CardHeader className="pt-4 pb-2 px-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold",
                    COMPACT_COLORS[rv.indexOf(expandedRole) % COMPACT_COLORS.length].bg,
                    COMPACT_COLORS[rv.indexOf(expandedRole) % COMPACT_COLORS.length].text
                  )}>{expandedRole.user_count}</div>
                  <div>
                    <CardTitle className="text-sm">{expandedRole.name}</CardTitle>
                    <p className="text-[11px] text-muted-foreground">{expandedRole.permission_count} permissions &middot; {expandedRole.description || "No description"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {expandedRole.is_editable && (
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); se(expandedRole); }} data-testid="edit-role-perms-btn">
                      <Pencil className="w-3 h-3 mr-1" /> Edit Permissions
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setExpandedRole(null); setRoleStaff([]); }}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-5 pb-4">
              {loadingStaff ? (
                <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
              ) : roleStaff.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-6">No staff assigned to this role</p>
              ) : (
                <div className="divide-y">
                  {roleStaff.map(s => (
                    <button key={s.id} className="w-full flex items-center gap-3 py-2.5 px-2 hover:bg-slate-50 rounded-lg transition-colors text-left"
                      onClick={(e) => { e.stopPropagation(); navigate(`/admin/staff/${s.id}`); }}
                      data-testid={`staff-link-${s.id}`}>
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600">
                        {(s.full_name || "?").charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{s.full_name}</p>
                        <p className="text-[11px] text-muted-foreground truncate">{s.email}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Inline Permission Editor */}
        {er && (
          <Card className="border-2 border-[#1e3a5f]/20 shadow-sm">
            <CardHeader className="pt-4 pb-2 px-5">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Pencil className="w-4 h-4 text-[#1e3a5f]" /> Editing — {er.name} ({ep.length} selected)
                </CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => ser(null)}>Cancel</Button>
                  <Button size="sm" className="text-xs h-7" onClick={sp} disabled={sv} data-testid="save-perms-btn">
                    {sv ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />} Save
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-5 pb-5">
              <PermissionEditor permsState={ep} setPermsState={sep} />
            </CardContent>
          </Card>
        )}

        {/* Action bar */}
        <div className="flex items-center justify-between pt-2">
          <p className="text-sm text-muted-foreground">{rv.length} roles configured</p>
          <Button variant="outline" size="sm" className="text-xs h-8" onClick={hr} data-testid="reset-rbac-btn">
            <RefreshCcw className="w-3 h-3 mr-1.5" /> Reset to Defaults
          </Button>
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteTarget} onOpenChange={() => { setDeleteTarget(null); setDeleteConfirmText(""); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600"><AlertTriangle className="w-5 h-5" /> Delete Role</DialogTitle>
              <DialogDescription>
                This will permanently delete <span className="font-semibold">{deleteTarget?.name}</span> and unassign all staff from this role. This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <p className="text-xs text-muted-foreground">Type <span className="font-mono font-bold text-foreground">DELETE</span> to confirm:</p>
              <Input value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE" className="h-9 text-sm font-mono" data-testid="delete-confirm-input" />
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => { setDeleteTarget(null); setDeleteConfirmText(""); }}>Cancel</Button>
              <Button size="sm" variant="destructive" disabled={deleteConfirmText !== "DELETE"} onClick={confirmDelete} data-testid="confirm-delete-role-btn">
                Delete Role
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  };

  const [activeTab, setActiveTab] = useState("roles");

  const PermissionMatrixAdvanced = ({ roles: matrixRoles, modules: matrixModules, getColor: gc, fetchData: fd }) => {
    const [modFilter, setModFilter] = useState("all");
    const [dirty, setDirty] = useState({});
    const [savingMatrix, setSavingMatrix] = useState(false);
    const [hovered, setHovered] = useState(null);

    const allPerms = Object.entries(matrixModules).flatMap(([, m]) => m.permissions);
    const filteredModules = modFilter === "all" ? Object.entries(matrixModules) : Object.entries(matrixModules).filter(([k]) => k === modFilter);

    const hasPerm = (role, perm) => {
      const key = `${role.id}:${perm}`;
      if (key in dirty) return dirty[key];
      return role.permissions.includes(perm);
    };

    const toggleCell = (role, perm) => {
      if (!role.is_editable) return;
      const key = `${role.id}:${perm}`;
      const current = hasPerm(role, perm);
      setDirty(prev => ({ ...prev, [key]: !current }));
    };

    const toggleModuleForRole = (role, mod) => {
      if (!role.is_editable) return;
      const perms = mod.permissions;
      const allOn = perms.every(p => hasPerm(role, p));
      const newDirty = { ...dirty };
      perms.forEach(p => { newDirty[`${role.id}:${p}`] = !allOn; });
      setDirty(newDirty);
    };

    const toggleAllForRole = (role) => {
      if (!role.is_editable) return;
      const allOn = allPerms.every(p => hasPerm(role, p));
      const newDirty = { ...dirty };
      allPerms.forEach(p => { newDirty[`${role.id}:${p}`] = !allOn; });
      setDirty(newDirty);
    };

    const togglePermForAll = (perm) => {
      const editableRoles = matrixRoles.filter(r => r.is_editable);
      const allOn = editableRoles.every(r => hasPerm(r, perm));
      const newDirty = { ...dirty };
      editableRoles.forEach(r => { newDirty[`${r.id}:${perm}`] = !allOn; });
      setDirty(newDirty);
    };

    const changeCount = Object.keys(dirty).filter(k => {
      const [roleId, perm] = k.split(":");
      const role = matrixRoles.find(r => r.id === roleId);
      return role && dirty[k] !== role.permissions.includes(perm);
    }).length;

    const saveMatrix = async () => {
      setSavingMatrix(true);
      try {
        const roleUpdates = {};
        for (const [key, value] of Object.entries(dirty)) {
          const [roleId, perm] = key.split(":");
          if (!roleUpdates[roleId]) {
            const role = matrixRoles.find(r => r.id === roleId);
            roleUpdates[roleId] = new Set(role ? role.permissions : []);
          }
          if (value) roleUpdates[roleId].add(perm);
          else roleUpdates[roleId].delete(perm);
        }
        await Promise.all(Object.entries(roleUpdates).map(([roleId, perms]) =>
          api.put(`/rbac/roles/${roleId}`, { permissions: [...perms] })
        ));
        toast.success(`${changeCount} permission change(s) saved`);
        setDirty({});
        fd();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to save");
      } finally {
        setSavingMatrix(false);
      }
    };

    const getRolePermCount = (role) => allPerms.filter(p => hasPerm(role, p)).length;

    return (
      <div className="space-y-4" data-testid="permission-matrix-advanced">
        {/* Controls Bar */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Select value={modFilter} onValueChange={setModFilter}>
              <SelectTrigger className="h-8 text-xs w-48" data-testid="matrix-module-filter">
                <Filter className="w-3 h-3 mr-1" /><SelectValue placeholder="All Modules" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All Modules</SelectItem>
                {Object.entries(matrixModules).map(([k, m]) => (
                  <SelectItem key={k} value={k} className="text-xs">{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Badge variant="outline" className="text-[10px]">{allPerms.length} permissions</Badge>
          </div>
          <div className="flex items-center gap-2">
            {changeCount > 0 && (
              <>
                <Badge className="text-[10px] bg-amber-100 text-amber-700 border-amber-200">{changeCount} unsaved change(s)</Badge>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setDirty({})} data-testid="matrix-discard-btn">Discard</Button>
                <Button size="sm" className="h-7 text-xs gap-1" onClick={saveMatrix} disabled={savingMatrix} data-testid="matrix-save-btn">
                  {savingMatrix ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save All
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 text-[10px] text-muted-foreground px-1">
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-emerald-400" /> Granted</div>
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-slate-100 border" /> Denied</div>
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-amber-400" /> Changed (unsaved)</div>
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-slate-300" /> Locked (system role)</div>
        </div>

        {/* Matrix Table */}
        <Card className="border shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              {/* Header */}
              <thead>
                <tr className="bg-muted/40 border-b">
                  <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground sticky left-0 bg-muted/40 z-10 min-w-[220px]">Permission</th>
                  {matrixRoles.map((r, i) => {
                    const c = gc(i);
                    return (
                      <th key={r.id} className="px-2 py-3 text-center min-w-[100px]">
                        <div className="flex flex-col items-center gap-1">
                          <button onClick={() => r.is_editable && toggleAllForRole(r)}
                            className={cn("text-[10px] font-bold px-2 py-0.5 rounded-full transition-colors", c.bg, c.text, r.is_editable && "hover:opacity-80 cursor-pointer")}
                            title={r.is_editable ? "Toggle all permissions" : "System role (locked)"}
                            data-testid={`matrix-role-header-${r.id}`}
                          >{r.name}</button>
                          <span className="text-[9px] text-muted-foreground">{getRolePermCount(r)}/{allPerms.length}</span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {filteredModules.map(([modKey, mod]) => (
                  <>
                    {/* Module Header Row */}
                    <tr key={`mod-${modKey}`} className="bg-muted/15">
                      <td className="px-4 py-2 sticky left-0 bg-muted/15 z-10">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{mod.label}</span>
                      </td>
                      {matrixRoles.map((r, i) => {
                        const c = gc(i);
                        const modAllOn = mod.permissions.every(p => hasPerm(r, p));
                        const modSomeOn = mod.permissions.some(p => hasPerm(r, p));
                        return (
                          <td key={r.id} className="px-2 py-2 text-center">
                            <button onClick={() => toggleModuleForRole(r, mod)}
                              className={cn("w-5 h-5 rounded border inline-flex items-center justify-center transition-colors",
                                r.is_editable ? "cursor-pointer hover:border-primary" : "cursor-default opacity-50",
                                modAllOn ? cn("border-emerald-400", c.bg) : modSomeOn ? "bg-emerald-100 border-emerald-200" : "border-border"
                              )}
                              title={`Toggle all ${mod.label} for ${r.name}`}
                              data-testid={`matrix-mod-toggle-${modKey}-${r.id}`}
                            >
                              {modAllOn ? <Check className="w-3 h-3 text-emerald-600" /> : modSomeOn ? <span className="w-1.5 h-1.5 rounded-sm bg-emerald-400" /> : null}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                    {/* Permission Rows */}
                    {mod.permissions.map(perm => {
                      const permLabel = perm.split(".").slice(1).join(".");
                      return (
                        <tr key={perm} className={cn("border-b border-border/40 transition-colors", hovered === perm && "bg-primary/[0.03]")}
                          onMouseEnter={() => setHovered(perm)} onMouseLeave={() => setHovered(null)}>
                          <td className="px-4 py-1.5 sticky left-0 bg-white z-10">
                            <div className="flex items-center justify-between group">
                              <span className="text-xs" title={perm}>{permLabel}</span>
                              <button onClick={() => togglePermForAll(perm)}
                                className="hidden group-hover:inline-flex text-[8px] text-muted-foreground hover:text-primary px-1 py-0.5 rounded bg-muted/50"
                                title="Toggle this permission for all editable roles" data-testid={`matrix-perm-toggle-all-${perm}`}>all</button>
                            </div>
                          </td>
                          {matrixRoles.map((r, i) => {
                            const c = gc(i);
                            const on = hasPerm(r, perm);
                            const key = `${r.id}:${perm}`;
                            const isChanged = key in dirty && dirty[key] !== r.permissions.includes(perm);
                            const locked = !r.is_editable;
                            return (
                              <td key={r.id} className="px-2 py-1.5 text-center">
                                <button
                                  onClick={() => toggleCell(r, perm)}
                                  disabled={locked}
                                  className={cn(
                                    "w-7 h-7 rounded-md border inline-flex items-center justify-center transition-all",
                                    locked && "cursor-not-allowed opacity-40",
                                    !locked && "cursor-pointer hover:scale-110",
                                    isChanged && "ring-2 ring-amber-400 ring-offset-1",
                                    on ? cn("bg-emerald-50 border-emerald-300") : "bg-slate-50 border-slate-200",
                                  )}
                                  title={locked ? `${r.name}: locked (system role)` : `${r.name}: ${on ? "Granted" : "Denied"} — click to toggle`}
                                  data-testid={`matrix-cell-${r.id}-${perm}`}
                                >
                                  {on ? <Check className={cn("w-4 h-4", isChanged ? "text-amber-600" : "text-emerald-500")} /> : (isChanged ? <X className="w-3 h-3 text-amber-500" /> : null)}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    );
  };

  const PermissionEditor = ({ permsState, setPermsState, readOnly }) => (
    <div className="space-y-4">
      {Object.entries(modules).map(([key, mod]) => (
        <div key={key}>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{mod.label}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
            {mod.permissions.map(p => (
              <button
                key={p}
                disabled={readOnly}
                onClick={() => !readOnly && togglePerm(p, permsState, setPermsState)}
                className={cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg border text-xs transition-all",
                  permsState.includes(p) ? "border-[#1e3a5f]/30 bg-[#1e3a5f]/5 text-[#1e3a5f] font-medium" : "border-slate-200 text-slate-500",
                  !readOnly && "hover:border-[#1e3a5f]/30 hover:bg-slate-50 cursor-pointer",
                  readOnly && "opacity-60 cursor-default"
                )}
                data-testid={`perm-toggle-${p}`}
              >
                <div className={cn("w-4 h-4 rounded border flex items-center justify-center shrink-0",
                  permsState.includes(p) ? "bg-[#1e3a5f] border-[#1e3a5f]" : "border-slate-300 bg-white"
                )}>
                  {permsState.includes(p) && <Check className="w-3 h-3 text-white" />}
                </div>
                {p.split(".")[1]}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="w-6 h-6 animate-spin text-primary" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="settings-rbac">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Platform Roles & Permissions</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage access control for your Odesie platform team</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-role-btn" className="btn-primary">
          <Plus className="w-4 h-4 mr-2" /> New Role
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-muted/50 p-1 rounded-lg w-fit">
        <button onClick={() => setActiveTab("roles")} className={cn("px-4 py-2 text-sm font-medium rounded-md transition-all", activeTab === "roles" ? "bg-white shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground")} data-testid="tab-roles">
          <Shield className="w-4 h-4 inline mr-2" />Roles & RBAC
        </button>
        <button onClick={() => setActiveTab("matrix")} className={cn("px-4 py-2 text-sm font-medium rounded-md transition-all", activeTab === "matrix" ? "bg-white shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground")} data-testid="tab-matrix">
          <BarChart3 className="w-4 h-4 inline mr-2" />Permission Matrix
        </button>
      </div>

      {/* Create Role Form */}
      {showCreate && (
        <Card className="border-2 border-primary/30 shadow-sm">
          <CardHeader className="pb-2 pt-4 px-5">
            <CardTitle className="text-sm flex items-center gap-2"><Plus className="w-4 h-4 text-primary" /> Create New Platform Role</CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Role Name *</Label><Input value={newRole.name} onChange={e => setNewRole({...newRole, name: e.target.value})} placeholder="e.g. Account Manager" className="h-9 text-sm" data-testid="new-role-name" /></div>
              <div className="space-y-1"><Label className="text-xs">Description</Label><Input value={newRole.description} onChange={e => setNewRole({...newRole, description: e.target.value})} placeholder="Brief description..." className="h-9 text-sm" data-testid="new-role-desc" /></div>
            </div>
            <div>
              <Label className="text-xs mb-2 block">Permissions ({newRole.permissions.length} selected)</Label>
              <PermissionEditor permsState={newRole.permissions} setPermsState={(fn) => setNewRole(prev => ({...prev, permissions: typeof fn === 'function' ? fn(prev.permissions) : fn}))} />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => { setShowCreate(false); setNewRole({ name: "", description: "", permissions: [] }); }}>Cancel</Button>
              <Button size="sm" onClick={handleCreate} disabled={creating} data-testid="save-new-role-btn">
                {creating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Create Role
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === "roles" && (
        <RolesView
          roles={roles}
          getColor={getColor}
          editingRole={editingRole}
          setEditingRole={setEditingRole}
          startEdit={startEdit}
          editPerms={editPerms}
          setEditPerms={setEditPerms}
          savePermissions={savePermissions}
          saving={saving}
          handleDelete={handleDelete}
          handleReset={handleReset}
          modules={modules}
          showCreate={showCreate}
        />
      )}

      {activeTab === "matrix" && (
        <PermissionMatrixAdvanced roles={roles} modules={modules} getColor={getColor} fetchData={fetchData} />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════
// CERTIFICATES SETTINGS
// ═══════════════════════════════════════════
const CertificatesSection = () => {
  const [certSettings, setCertSettings] = useState({
    auto_generate: true, include_logo: true, include_signature: true,
    signature_name: "", signature_title: "", expiry_enabled: false, expiry_months: 12,
    verification_enabled: true, template: "professional"
  });

  return (
    <div className="space-y-5 max-w-3xl" data-testid="settings-certificates">
      <SectionHeader icon={Award} title="Certificate Settings" desc="Configure certificate generation, appearance, and verification" />
      <HelpTip title="Certificate Management">Certificates are automatically generated when learners complete courses. They include a unique verification code for third-party validation. Configure expiry for compliance-related certificates.</HelpTip>
      <Card className="border shadow-none">
        <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Generation Settings</CardTitle></CardHeader>
        <CardContent className="px-5 pb-5 space-y-3">
          <div className="flex items-center justify-between p-3 rounded-lg border">
            <div><p className="text-sm font-medium">Auto-Generate Certificates</p><p className="text-[10px] text-muted-foreground">Automatically create certificates upon course completion</p></div>
            <Switch checked={certSettings.auto_generate} onCheckedChange={v => setCertSettings({ ...certSettings, auto_generate: v })} />
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg border">
            <div><p className="text-sm font-medium">Include Company Logo</p><p className="text-[10px] text-muted-foreground">Show your organization logo on certificates</p></div>
            <Switch checked={certSettings.include_logo} onCheckedChange={v => setCertSettings({ ...certSettings, include_logo: v })} />
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg border">
            <div><p className="text-sm font-medium">Online Verification</p><p className="text-[10px] text-muted-foreground">Allow third parties to verify certificates via public URL</p></div>
            <Switch checked={certSettings.verification_enabled} onCheckedChange={v => setCertSettings({ ...certSettings, verification_enabled: v })} />
          </div>
        </CardContent>
      </Card>
      <Card className="border shadow-none">
        <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Signature</CardTitle></CardHeader>
        <CardContent className="px-5 pb-5 space-y-4">
          <div className="flex items-center justify-between p-3 rounded-lg border mb-3">
            <div><p className="text-sm font-medium">Include Signature</p><p className="text-[10px] text-muted-foreground">Add an authorized signature to certificates</p></div>
            <Switch checked={certSettings.include_signature} onCheckedChange={v => setCertSettings({ ...certSettings, include_signature: v })} />
          </div>
          {certSettings.include_signature && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs">Signatory Name</Label><Input value={certSettings.signature_name} onChange={e => setCertSettings({ ...certSettings, signature_name: e.target.value })} placeholder="e.g. John Smith" className="h-9 text-sm" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Signatory Title</Label><Input value={certSettings.signature_title} onChange={e => setCertSettings({ ...certSettings, signature_title: e.target.value })} placeholder="e.g. Director of Training" className="h-9 text-sm" /></div>
            </div>
          )}
        </CardContent>
      </Card>
      <Card className="border shadow-none">
        <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Expiry Settings</CardTitle></CardHeader>
        <CardContent className="px-5 pb-5 space-y-3">
          <div className="flex items-center justify-between p-3 rounded-lg border">
            <div><p className="text-sm font-medium">Certificate Expiry</p><p className="text-[10px] text-muted-foreground">Set certificates to expire after a specified period</p></div>
            <Switch checked={certSettings.expiry_enabled} onCheckedChange={v => setCertSettings({ ...certSettings, expiry_enabled: v })} />
          </div>
          {certSettings.expiry_enabled && (
            <div className="space-y-1.5"><Label className="text-xs">Expiry Period (months)</Label><Input type="number" value={certSettings.expiry_months} onChange={e => setCertSettings({ ...certSettings, expiry_months: parseInt(e.target.value) || 12 })} className="h-9 text-sm w-40" /><p className="text-[10px] text-muted-foreground">OSHA standard: 12 months for annual certifications</p></div>
          )}
        </CardContent>
      </Card>
      <Card className="border shadow-none">
        <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Template</CardTitle></CardHeader>
        <CardContent className="px-5 pb-5">
          <div className="grid grid-cols-3 gap-3">
            {[
              { id: "professional", label: "Professional", desc: "Clean corporate design" },
              { id: "classic", label: "Classic", desc: "Traditional certificate style" },
              { id: "modern", label: "Modern", desc: "Contemporary minimal design" },
            ].map(t => (
              <button key={t.id} onClick={() => setCertSettings({ ...certSettings, template: t.id })}
                className={`p-4 rounded-lg border text-left transition-all ${certSettings.template === t.id ? "border-primary border-2 bg-primary/5" : "hover:border-primary/30"}`}>
                <p className="text-sm font-medium">{t.label}</p>
                <p className="text-[10px] text-muted-foreground">{t.desc}</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
      <div className="flex justify-end"><Button onClick={() => toast.success("Certificate settings saved")} className="gap-1.5"><Save className="w-4 h-4" /> Save Settings</Button></div>
    </div>
  );
};

// ═══════════════════════════════════════════
// SUBSCRIPTION SETTINGS
// ═══════════════════════════════════════════
const SubscriptionSection = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const base = user?.role === "super_admin" ? "/admin" : "/tenant";

  return (
    <div className="space-y-5 max-w-3xl" data-testid="settings-subscription">
      <SectionHeader icon={FileText} title="Subscription Management" desc="View and manage your current subscription" />
      <HelpTip title="Subscription Details">Your subscription determines the features and limits available to your organization. Upgrade at any time to unlock more capabilities.</HelpTip>
      <Card className="border shadow-none">
        <CardContent className="p-6 text-center space-y-4">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto"><FileText className="w-7 h-7 text-primary" /></div>
          <div>
            <h3 className="text-lg font-bold">Professional Plan</h3>
            <p className="text-sm text-muted-foreground">Your current subscription is active and in good standing</p>
          </div>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => navigate(`${base}/billing`)} className="gap-1.5" data-testid="go-to-billing-btn">View Plans & Billing <ChevronRight className="w-3.5 h-3.5" /></Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ═══════════════════════════════════════════
// AUDIT LOGS
// ═══════════════════════════════════════════
const AuditSection = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [retention, setRetention] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filterAction, setFilterAction] = useState("");
  const [filterResource, setFilterResource] = useState("");
  const [filterDays, setFilterDays] = useState("30");
  const [activeTab, setActiveTab] = useState("logs");

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: page * 50, limit: 50, days: filterDays });
      if (filterAction) params.set("action", filterAction);
      if (filterResource) params.set("resource_type", filterResource);
      const [lRes, sRes, rRes] = await Promise.all([
        api.get(`/audit-logs?${params}`),
        api.get("/audit-logs/stats"),
        api.get("/audit-logs/retention"),
      ]);
      setLogs(lRes.data.logs || []);
      setTotal(lRes.data.total || 0);
      setStats(sRes.data);
      setRetention(rRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, filterAction, filterResource, filterDays]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleRetentionSave = async () => {
    try { await api.put("/audit-logs/retention", retention); toast.success("Retention policy updated"); }
    catch (e) { toast.error("Failed to save"); }
  };

  const s = stats || { total: 0, today: 0, this_week: 0, top_actors: [], action_breakdown: [] };
  const ACTION_COLORS = { create: "bg-green-50 text-green-700 border-green-200", update: "bg-blue-50 text-blue-700 border-blue-200", delete: "bg-red-50 text-red-700 border-red-200" };
  const getActionColor = (action) => {
    if (action.startsWith("create") || action.includes("generate")) return ACTION_COLORS.create;
    if (action.startsWith("delete") || action.includes("purge")) return ACTION_COLORS.delete;
    return ACTION_COLORS.update;
  };

  return (
    <div className="space-y-5" data-testid="settings-audit">
      <SectionHeader icon={ScrollText} title="Audit Logs" desc="Complete activity trail for compliance and security monitoring" />
      <HelpTip title="Audit Log Retention Policy">Per industry best practices (SOX, HIPAA, OSHA): retain logs for 365 days minimum. Critical actions (compliance, safety) should be kept for 7 years. Auto-archival moves old logs to cold storage after 90 days.</HelpTip>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border shadow-none"><CardContent className="p-3 flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center"><ScrollText className="w-4 h-4 text-slate-600" /></div>
          <div><p className="text-lg font-bold leading-none">{s.total.toLocaleString()}</p><p className="text-[10px] text-muted-foreground">Total Events</p></div>
        </CardContent></Card>
        <Card className="border shadow-none"><CardContent className="p-3 flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center"><Activity className="w-4 h-4 text-blue-600" /></div>
          <div><p className="text-lg font-bold leading-none">{s.today}</p><p className="text-[10px] text-muted-foreground">Today</p></div>
        </CardContent></Card>
        <Card className="border shadow-none"><CardContent className="p-3 flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center"><Calendar className="w-4 h-4 text-amber-600" /></div>
          <div><p className="text-lg font-bold leading-none">{s.this_week}</p><p className="text-[10px] text-muted-foreground">This Week</p></div>
        </CardContent></Card>
        <Card className="border shadow-none"><CardContent className="p-3 flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center"><Users className="w-4 h-4 text-green-600" /></div>
          <div><p className="text-lg font-bold leading-none">{s.top_actors.length}</p><p className="text-[10px] text-muted-foreground">Active Users</p></div>
        </CardContent></Card>
      </div>
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-muted/50 h-9">
          <TabsTrigger value="logs" className="text-xs gap-1.5"><ScrollText className="w-3.5 h-3.5" /> Activity Log</TabsTrigger>
          <TabsTrigger value="retention" className="text-xs gap-1.5"><Settings className="w-3.5 h-3.5" /> Retention Policy</TabsTrigger>
        </TabsList>
      </Tabs>
      {activeTab === "logs" ? (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={filterDays} onValueChange={v => { setFilterDays(v); setPage(0); }}>
              <SelectTrigger className="w-[130px] h-9 text-xs"><Calendar className="w-3.5 h-3.5 mr-1" /><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="7">Last 7 days</SelectItem><SelectItem value="30">Last 30 days</SelectItem><SelectItem value="90">Last 90 days</SelectItem><SelectItem value="365">Last year</SelectItem></SelectContent>
            </Select>
            <Input placeholder="Filter by action..." value={filterAction} onChange={e => { setFilterAction(e.target.value); setPage(0); }} className="h-9 text-sm w-44" />
            <Input placeholder="Filter by resource..." value={filterResource} onChange={e => { setFilterResource(e.target.value); setPage(0); }} className="h-9 text-sm w-44" />
            {(filterAction || filterResource) && <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={() => { setFilterAction(""); setFilterResource(""); }}><X className="w-3.5 h-3.5 mr-1" /> Clear</Button>}
            <span className="text-xs text-muted-foreground ml-auto">{total.toLocaleString()} events</span>
          </div>
          <Card className="border shadow-none">
            <CardContent className="p-0">
              <div className="grid grid-cols-12 gap-2 text-[10px] text-muted-foreground uppercase tracking-wider font-semibold px-4 py-2.5 border-b bg-muted/30">
                <span className="col-span-2">Timestamp</span><span className="col-span-2">User</span>
                <span className="col-span-2">Action</span><span className="col-span-2">Resource</span>
                <span className="col-span-3">Details</span><span className="col-span-1">Status</span>
              </div>
              <div className="max-h-[500px] overflow-y-auto">
                {loading ? <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div> :
                 logs.length === 0 ? <div className="text-center py-12 text-sm text-muted-foreground">No audit events found</div> :
                 logs.map(log => (
                  <div key={log.id} className="grid grid-cols-12 gap-2 items-center px-4 py-2.5 border-b hover:bg-muted/20 transition-colors text-sm">
                    <span className="col-span-2 text-[11px] text-muted-foreground font-mono">{new Date(log.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    <div className="col-span-2 min-w-0"><p className="text-xs font-medium truncate">{log.user_name || "System"}</p><p className="text-[10px] text-muted-foreground truncate">{log.ip_address}</p></div>
                    <span className="col-span-2"><Badge className={`text-[9px] px-1.5 py-0 border ${getActionColor(log.action)}`}>{log.action}</Badge></span>
                    <span className="col-span-2 text-xs">{log.resource_type}{log.resource_id ? ` #${log.resource_id.slice(0, 6)}` : ""}</span>
                    <span className="col-span-3 text-[11px] text-muted-foreground truncate">{log.details}</span>
                    <span className="col-span-1">{log.http_status ? <Badge variant="outline" className={`text-[9px] ${log.http_status < 400 ? "text-green-600" : "text-red-600"}`}>{log.http_status}</Badge> : <Badge variant="outline" className="text-[9px]">OK</Badge>}</span>
                  </div>
                ))}
              </div>
              {total > 50 && (
                <div className="px-4 py-2.5 border-t flex items-center justify-between">
                  <Button variant="outline" size="sm" className="text-xs h-8" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</Button>
                  <span className="text-xs text-muted-foreground">Page {page + 1} of {Math.ceil(total / 50)}</span>
                  <Button variant="outline" size="sm" className="text-xs h-8" disabled={(page + 1) * 50 >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : retention && (
        <Card className="border shadow-none">
          <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Retention Policy Configuration</CardTitle></CardHeader>
          <CardContent className="px-5 pb-5 space-y-4">
            <HelpTip title="Industry Standard Retention">OSHA: 5 years for training records. SOX: 7 years for audit trails. HIPAA: 6 years. GDPR: Data minimization principle applies. Configure based on your most stringent requirement.</HelpTip>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs">Retention Period (days)</Label><Input type="number" value={retention.days} onChange={e => setRetention({ ...retention, days: parseInt(e.target.value) || 365 })} className="h-9 text-sm" /><p className="text-[10px] text-muted-foreground">Industry standard: 365 days (OSHA), 2555 days/7 years (SOX)</p></div>
              <div className="space-y-1.5"><Label className="text-xs">Auto-Archive After (days)</Label><Input type="number" value={retention.archive_after_days} onChange={e => setRetention({ ...retention, archive_after_days: parseInt(e.target.value) || 90 })} className="h-9 text-sm" /><p className="text-[10px] text-muted-foreground">Move to cold storage after this period</p></div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-lg border"><div><p className="text-sm font-medium">Auto-Archive</p><p className="text-[10px] text-muted-foreground">Automatically archive logs past retention period</p></div><Switch checked={retention.auto_archive} onCheckedChange={v => setRetention({ ...retention, auto_archive: v })} /></div>
              <div className="flex items-center justify-between p-3 rounded-lg border"><div><p className="text-sm font-medium">GDPR Compliant</p><p className="text-[10px] text-muted-foreground">Respect data deletion requests for PII in logs</p></div><Switch checked={retention.gdpr_compliant} onCheckedChange={v => setRetention({ ...retention, gdpr_compliant: v })} /></div>
              <div className="flex items-center justify-between p-3 rounded-lg border"><div><p className="text-sm font-medium">PII Masking</p><p className="text-[10px] text-muted-foreground">Mask personal identifiable information in archived logs</p></div><Switch checked={retention.pii_masking} onCheckedChange={v => setRetention({ ...retention, pii_masking: v })} /></div>
            </div>
            <div className="flex justify-end"><Button onClick={handleRetentionSave} className="gap-1.5"><Save className="w-4 h-4" /> Save Policy</Button></div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════
// MAIN SETTINGS PAGE (ROUTER)
// ═══════════════════════════════════════════
const SettingsPage = () => {
  const { section } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const variant = user?.role === "super_admin" ? "super_admin" : "tenant_admin";
  const base = user?.role === "super_admin" ? "/admin" : "/tenant";

  const sectionMap = {
    profile: { component: ProfileSection, title: "Profile & Preferences" },
    account: { component: AccountSection, title: "Organization Profile" },
    security: { component: SecuritySection, title: "Security Settings" },
    notifications: { component: NotificationsSection, title: "Notification Preferences" },
    departments: { component: DepartmentsSection, title: "Departments" },
    branding: { component: BrandingSection, title: "Branding & Appearance" },
    integrations: { component: IntegrationsSection, title: "Integrations" },
    rbac: { component: RBACSection, title: "Roles & Permissions" },
    certificates: { component: CertificatesSection, title: "Certificate Settings" },
    subscription: { component: SubscriptionSection, title: "Subscription" },
    audit: { component: AuditSection, title: "Audit Logs" },
  };

  const current = sectionMap[section];

  if (!current) {
    return (
      <AdminLayout title={`Settings`} variant={variant}>
        <div className="space-y-5 max-w-3xl">
          <Button variant="ghost" size="sm" className="text-xs gap-1.5 -ml-2" onClick={() => navigate(`${base}/settings`)}>
            <ChevronLeft className="w-3.5 h-3.5" /> All Settings
          </Button>
          <Card className="border shadow-none">
            <CardContent className="p-8 text-center">
              <Settings className="w-12 h-12 mx-auto text-muted-foreground/20 mb-3" />
              <p className="text-sm text-muted-foreground">This section is not available. Please select a valid settings page.</p>
              <Button variant="outline" size="sm" className="mt-4 text-xs" onClick={() => navigate(`${base}/settings`)}>Back to Settings</Button>
            </CardContent>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  const Comp = current.component;
  return (
    <AdminLayout title={current.title} variant={variant}>
      <Button variant="ghost" size="sm" className="text-xs gap-1.5 -ml-2 mb-3" onClick={() => navigate(`${base}/settings`)} data-testid="back-to-settings">
        <ChevronLeft className="w-3.5 h-3.5" /> All Settings
      </Button>
      <Comp />
    </AdminLayout>
  );
};

export default SettingsPage;
